import { useState } from 'react';
import { GlobalSearchOption } from './useGlobalSearchOptions';

/** The selected option and the three keys a search field hands its list. */
export interface GlobalSearchKeys {
    activeOptionId: string | undefined;
    onArrowDown: () => boolean;
    onArrowUp: () => boolean;
    onEnter: () => boolean;
}

/**
 * The one selection a search list keeps, shared by every surface that draws one: the header's box,
 * the narrow sheet and the new design's dialog.
 *
 * It lives beside the field and not in the panel, because the field is what names the selected
 * option in `aria-activedescendant`. The selection is a derivation — the option last moved to, else
 * the first one drawn — so results landing never have to be written back, and no frame is painted
 * with the list and the mark on it disagreeing. That is why the keys are asked for with the options
 * drawn this render, rather than the options being handed in up front.
 */
export function useGlobalSearchSelection(query: string) {
    const [pickedOptionId, setPickedOptionId] = useState<string>();
    // A move made against the last question must not carry over: its row may well still be in the
    // new answer, and would then hold the mark and Enter against the hit the reader typed the rest
    // of the word to find.
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers
    const [pickedFor, setPickedFor] = useState(query);
    if (pickedFor !== query) {
        setPickedFor(query);
        setPickedOptionId(undefined);
    }

    const keysFor = (options: GlobalSearchOption[], firstId: string | undefined): GlobalSearchKeys => {
        const activeOptionId = options.some((option) => option.id === pickedOptionId) ? pickedOptionId : firstId;

        const move = (delta: number) => {
            if (options.length === 0) {
                return false;
            }
            const at = activeOptionId === undefined ? -1 : options.findIndex((option) => option.id === activeOptionId);
            const next =
                at === -1
                    ? delta > 0
                        ? options[0]
                        : options[options.length - 1]
                    : options[Math.min(Math.max(at + delta, 0), options.length - 1)];
            setPickedOptionId(next.id);
            // Keys only: scrolling a row the pointer is already on moves the list out from under it.
            document.getElementById(next.id)?.scrollIntoView({ block: 'nearest' });
            return true;
        };

        return {
            activeOptionId,
            onArrowDown: () => move(1),
            onArrowUp: () => move(-1),
            // Falls through to the field, and so to the results page, only with nothing drawn to open.
            onEnter: () => {
                const option = options.find((item) => item.id === activeOptionId);
                if (!option) {
                    return false;
                }
                option.select();
                return true;
            },
        };
    };

    return {
        /** A closed list has no options left to be on, and the next opening starts over. */
        forget: () => setPickedOptionId(undefined),
        keysFor,
    };
}
